import { Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { formatNumberUsingUnit } from '../../../utils/formatUtils';
import { fiscalMonths } from '../../../constants/utils';

// TABLE 1 - PERFORMANCE OVERVIEW SERVICES
/**
 * Function to combine plan data and comparison data with branch name mapping
 * @param {Array} planData - Plan data from API
 * @param {Array} comparisonData - Comparison data from API
 * @param {Array} storesData - Store/branch information
 * @returns {Array} Combined and transformed data
 */
export const combineData = (planData, comparisonData, storesData = []) => {
  if (!planData.length && !comparisonData.length) return [];

  // Create a branch name lookup map using store_id/branch_number
  const branchNameMap = new Map();
  if (storesData && storesData.length > 0) {
    storesData.forEach((store) => {
      branchNameMap.set(store.branch_number, store.branch_name);
    });
  }

  const storeMap = new Map();

  // Process both plan and comparison data
  const processDataItem = (item, isComparisonData = false) => {
    const branchName = branchNameMap.get(parseInt(item.store_id, 10)) || `Branch ${item.store_id}`;

    if (storeMap.has(item.store_id)) {
      // Update existing record
      const storeData = storeMap.get(item.store_id);
      if (isComparisonData) {
        storeData.compare_perc = item.opps?.compare_perc;
      } else {
        storeData.opps_perc_to_plan = item.opps_perc_to_plan;
        storeData.spend_to_plan_ytd = item.spend_to_plan_ytd;
        storeData.spend_plan_full_year = item.spend_plan_full_year;
      }
      storeMap.set(item.store_id, storeData);
    } else {
      // Create new record
      storeMap.set(item.store_id, {
        store_id: branchName,
        original_store_id: item.store_id,
        opps_perc_to_plan: isComparisonData ? null : item.opps_perc_to_plan,
        spend_to_plan_ytd: isComparisonData ? null : item.spend_to_plan_ytd,
        spend_plan_full_year: isComparisonData ? null : item.spend_plan_full_year,
        compare_perc: isComparisonData ? item.opps?.compare_perc : null,
      });
    }
  };

  // Process all data
  planData.forEach((item) => processDataItem(item, false));
  comparisonData.forEach((item) => processDataItem(item, true));

  // Convert map to array and sort by branch name
  return Array.from(storeMap.values()).sort((a, b) => a.store_id.localeCompare(b.store_id));
};

// Cell renderers for Table 1
const PlanPercentCell = ({ value }) => {
  const content = value !== null ? formatNumberUsingUnit(value, '%') : 'N/A';
  return (
    <div style={{ textAlign: 'center' }}>
      <span style={{ fontWeight: value > 100 ? '700' : 'normal' }}>{content}</span>
    </div>
  );
};

PlanPercentCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
};

const BranchNameCell = ({ value }) => {
  return (
    <div style={{ textAlign: 'left' }}>
      <span>{value}</span>
    </div>
  );
};

BranchNameCell.propTypes = {
  value: PropTypes.string,
};

// Define common cell prop types
const cellPropType = PropTypes.shape({
  value: PropTypes.any,
  row: PropTypes.shape({
    original: PropTypes.object,
  }),
});

// Create a generic table cell wrapper function
const createTableCell = (CellComponent) => {
  const TableCell = ({ cell }) => <CellComponent value={cell.value} />;
  TableCell.propTypes = { cell: cellPropType.isRequired };
  TableCell.displayName = `TableCell(${CellComponent.displayName || CellComponent.name || 'Component'})`;
  return TableCell;
};

// Create table cell components using the wrapper
const TablePlanPercentCell = createTableCell(PlanPercentCell);
const TableBranchNameCell = createTableCell(BranchNameCell);

// Define percentage columns for DRY approach
const createPercentColumn = (headerText, accessor) => ({
  Header: headerText,
  accessor,
  Cell: TablePlanPercentCell,
});

/**
 * Define table columns for Performance Overview Table (Table 1)
 */
export const performanceOverviewColumns = [
  {
    Header: () => (
      <Typography sx={{ textAlign: 'left', fontWeight: 'bold', flex: 1 }}>Branch Name</Typography>
    ),
    accessor: 'store_id',
    Cell: TableBranchNameCell,
    disableSortBy: false,
  },
  createPercentColumn('% to Plan', 'opps_perc_to_plan'),
  createPercentColumn('% to Previous Year', 'compare_perc'),
  createPercentColumn('% Spend to Plan YTD', 'spend_to_plan_ytd'),
  createPercentColumn('% Spend to Plan Full Year', 'spend_plan_full_year'),
];

// TABLE 2 - OPPORTUNITIES TO PLAN SERVICES
/**
 * Cell component for Branch Name column in Table 2
 */
const BranchNameCellTable2 = ({ cell }) => (
  <div style={{ textAlign: 'left' }}>
    <span>{cell.value}</span>
  </div>
);

BranchNameCellTable2.propTypes = {
  cell: PropTypes.shape({
    value: PropTypes.string,
  }).isRequired,
};

/**
 * Cell component for percentage columns in Table 2
 */
const PercentCellTable2 = ({ cell }) => (
  <div style={{ textAlign: 'center' }}>
    <span>{formatNumberUsingUnit(cell.value, '%')}</span>
  </div>
);

PercentCellTable2.propTypes = {
  cell: PropTypes.shape({
    value: PropTypes.number,
  }).isRequired,
};

/**
 * Transform the API response data into table format for Opportunities to Plan
 * @param {Array} apiData - Raw API data
 * @param {Array} userStores - User store information
 * @returns {Array} Transformed table data
 */
export const transformOppsToPlantData = (apiData, userStores) => {
  if (!apiData || apiData.length === 0) {
    return [];
  }

  // Create a branch name lookup map
  const branchNameMap = new Map();
  if (userStores && userStores.length > 0) {
    userStores.forEach((store) => {
      branchNameMap.set(store.branch_number, store.branch_name);
    });
  }

  // Group data by store_id
  const groupedByStore = apiData.reduce((acc, item) => {
    if (!acc[item.store_id]) {
      acc[item.store_id] = [];
    }
    acc[item.store_id].push(item);
    return acc;
  }, {});

  // Transform each store's data into a row
  const tableData = Object.entries(groupedByStore).map(([storeId, storeData]) => {
    // Sort by fiscal_week to ensure correct order
    const sortedData = storeData.sort((a, b) => a.fiscal_week - b.fiscal_week);

    // Get store name from userStores
    const branchName = branchNameMap.get(parseInt(storeId, 10)) || `Branch ${storeId}`;

    // Build the row object
    const row = {
      store_id: parseInt(storeId),
      branch_name: branchName,
      ytd_perc_to_plan: sortedData[0]?.ytd_perc_opps_to_plan || 0,
      rolling_5_week: sortedData[0]?.rolling_5_weeks_perc_opps_to_plan || 0,
    };

    // Add weekly data with metadata for column generation
    sortedData.forEach((weekData) => {
      const weekKey = `fw_${weekData.fiscal_week}`;
      row[weekKey] = weekData.perc_opps_to_plan || 0;

      // Store metadata for this week (will be used by column generator)
      if (!row._weekMetadata) {
        row._weekMetadata = {};
      }
      row._weekMetadata[weekKey] = {
        fiscal_month: weekData.fiscal_month,
        fiscal_week: weekData.fiscal_week,
      };
    });

    return row;
  });

  // Sort by branch name
  return tableData.sort((a, b) => a.branch_name.localeCompare(b.branch_name));
};

/**
 * Generate dynamic columns based on the data for Opportunities to Plan
 * @param {Array} data - Transformed table data
 * @returns {Array} Column definitions for the table
 */
export const generateOppsToPlantColumns = (data) => {
  if (!data || data.length === 0) {
    return [];
  }

  // Base columns
  const columns = [
    {
      Header: () => (
        <Typography sx={{ textAlign: 'left', fontWeight: 'bold', width: 156 }}>
          Branch Name
        </Typography>
      ),
      accessor: 'branch_name',
      disableSortBy: false,
      Cell: BranchNameCellTable2,
    },
    {
      Header: () => (
        <Typography sx={{ textAlign: 'center', fontWeight: 'bold', minWidth: 130 }}>
          YTD % to Plan
        </Typography>
      ),
      accessor: 'ytd_perc_to_plan',
      disableSortBy: false,
      Cell: PercentCellTable2,
    },
  ];

  // Extract week metadata from the first row
  const firstRow = data[0];
  const weekMetadata = firstRow._weekMetadata || {};

  // Extract all week keys and sort them
  const weekKeys = Object.keys(firstRow)
    .filter((key) => key.startsWith('fw_'))
    .sort((a, b) => {
      const weekA = parseInt(a.split('_')[1], 10);
      const weekB = parseInt(b.split('_')[1], 10);
      return weekA - weekB;
    });

  // Add week columns with fiscal month from API data
  weekKeys.forEach((weekKey) => {
    const metadata = weekMetadata[weekKey];

    if (!metadata) {
      return;
    }

    const fiscalMonth = fiscalMonths[metadata.fiscal_month] || 'Unknown';
    const weekNumber = metadata.fiscal_week;
    const formattedWeek = weekNumber <= 9 ? `W0${weekNumber}` : `W${weekNumber}`;

    columns.push({
      Header: () => (
        <Typography sx={{ textAlign: 'center', fontWeight: 'bold', minWidth: 100 }}>
          {`${fiscalMonth} ${formattedWeek}`}
        </Typography>
      ),
      accessor: weekKey,
      disableSortBy: false,
      Cell: PercentCellTable2,
    });
  });

  // Add rolling 5 week column
  columns.push({
    Header: () => (
      <Typography sx={{ textAlign: 'center', fontWeight: 'bold', minWidth: 130 }}>
        Rolling 5 Week
      </Typography>
    ),
    accessor: 'rolling_5_week',
    disableSortBy: false,
    Cell: PercentCellTable2,
  });

  return columns;
};

// PropTypes for type checking
generateOppsToPlantColumns.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      store_id: PropTypes.number,
      branch_name: PropTypes.string,
      ytd_perc_to_plan: PropTypes.number,
      rolling_5_week: PropTypes.number,
      _weekMetadata: PropTypes.object,
    })
  ),
};

transformOppsToPlantData.propTypes = {
  apiData: PropTypes.arrayOf(
    PropTypes.shape({
      store_id: PropTypes.number,
      fiscal_year: PropTypes.number,
      fiscal_month: PropTypes.number,
      fiscal_week: PropTypes.number,
      perc_opps_to_plan: PropTypes.number,
      ytd_perc_opps_to_plan: PropTypes.number,
      rolling_5_weeks_perc_opps_to_plan: PropTypes.number,
    })
  ),
  userStores: PropTypes.arrayOf(
    PropTypes.shape({
      branch_number: PropTypes.number,
      branch_name: PropTypes.string,
    })
  ),
};

combineData.propTypes = {
  planData: PropTypes.arrayOf(PropTypes.object),
  comparisonData: PropTypes.arrayOf(PropTypes.object),
  storesData: PropTypes.arrayOf(
    PropTypes.shape({
      branch_number: PropTypes.number,
      branch_name: PropTypes.string,
    })
  ),
};
